import { useMemo } from "react";
import Card from "shared/Card";
import { TabContainer, Tab } from "shared/TabContainer";
import styles from "./index.module.css";
import Security from "./Security/Security";
import Session from "./Session/Session";
import Location from "./Location/Location";
import EventInfo from "./EventInfo/EventInfo";
import History from "./History/History";
import Setup from "./Setup/Setup";
import Loader from "shared/Loader";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export default function Details({ eventInfo, refreshEventData, isLoading }) {
  const { validTimeZone, timezoneAbbr } = useMemo(() => {
    const tzInfo = eventInfo?.timeZones?.find(
      (tz) => tz.name === eventInfo?.timeZone
    );
    return {
      validTimeZone: tzInfo?.convertAs || "America/Los_Angeles",
      timezoneAbbr: tzInfo?.abbreviation || "PT",
    };
  }, [eventInfo?.timeZone, eventInfo?.timeZones]);

  if (isLoading || !eventInfo) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <Loader />
      </div>
    );
  }

  const {
    eventId,
    displayName,
    scanningGuid,
    scanningSessions,
    scanningLocations,
    scanningSecurities,
    scanningHistory,
    checkInDates = [],
  } = eventInfo;

  const checkInStartsOn =
    checkInDates.find((a) => a.dateTypeId === 22)?.startsOn ||
    eventInfo.newCheckInDates?.checkInStarts ||
    null;

  const checkInEndsOn =
    checkInDates.find((a) => a.dateTypeId === 23)?.startsOn ||
    eventInfo.newCheckInDates?.checkInEnds ||
    null;
  function formatDateTime(utcDate) {
    if (!utcDate) return "N/A";
    const date = dayjs.utc(utcDate);
    if (!date.isValid()) return "Invalid Date";
    return date.tz(validTimeZone).format(`MM/DD/YY hh:mm A [${timezoneAbbr}]`);
  }

  return (
    <div>
      <header className={styles.eventHeader}>
        <span className={styles.headerEventType}>{displayName}</span>
        <h1 style={{ marginBottom: "1rem" }}>
          {eventInfo.venue?.address?.city || "Unknown City"}
          {eventInfo.venue?.address?.state
            ? ` ${eventInfo.venue.address.state}`
            : ""}
          {" – "}
          {eventInfo.eventStartsOn
            ? dayjs(eventInfo.eventStartsOn)
                .tz(validTimeZone)
                .format("MMMM Do, YYYY")
            : ""}
          {!checkInStartsOn && (
            <span style={{ color: "red", marginLeft: "0.5rem" }}>
              (Error: No check-in date available)
            </span>
          )}
        </h1>
      </header>

      <Card>
        <EventInfo eventInfo={eventInfo} refreshEventData={refreshEventData} />
      </Card>

      <TabContainer>
        <Tab title="Setup" value="Setup">
          <Setup
            eventId={eventId}
            scanningGuid={scanningGuid}
            checkInStartsOn={checkInStartsOn}
            checkInEndsOn={checkInEndsOn}
            formatDateTime={formatDateTime}
            timezone={validTimeZone}
            timezoneAbbr={timezoneAbbr}
          />
          <Session
            eventId={eventId}
            scanningSessions={scanningSessions}
            formatDateTime={formatDateTime}
            refreshEventData={refreshEventData}
            timezoneAbbr={timezoneAbbr}
          />
          <Location
            eventId={eventId}
            scanningLocations={scanningLocations}
            refreshEventData={refreshEventData}
            timezoneAbbr={timezoneAbbr}
          />
        </Tab>
        <Tab title="Security" value="security">
          <Security
            eventId={eventId}
            scanningSecurities={scanningSecurities}
            refreshEventData={refreshEventData}
          />
        </Tab>
        <Tab title="History" value="history">
          <History
            eventId={eventId}
            scanningHistory={scanningHistory}
            timeZone={validTimeZone}
            refreshEventData={refreshEventData}
            timezoneAbbr={timezoneAbbr}
          />
        </Tab>
      </TabContainer>
    </div>
  );
}
